import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useToast } from "@/hooks/use-toast";
import WorkbenchLayout from "@/components/layout/workbench-layout";
import StatsCards from "@/components/dashboard/stats-cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Briefcase, Plus, Search, Globe, Pencil, Trash2, ArrowRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { Sponsor } from "@shared/schema";

interface SponsorFormState {
  name: string;
  website: string;
  notes: string;
}

const EMPTY_FORM: SponsorFormState = { name: "", website: "", notes: "" };

export default function Workbench() {
  usePageTitle("Sponsor Workbench");
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Sponsor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Sponsor | null>(null);
  const [form, setForm] = useState<SponsorFormState>(EMPTY_FORM);

  const { data: sponsors = [], isLoading } = useQuery<Sponsor[]>({
    queryKey: ["/api/sponsors"],
  });

  const createSponsor = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = { name: form.name.trim() };
      if (form.website.trim()) body.website = form.website.trim();
      if (form.notes.trim()) body.notes = form.notes.trim();
      return apiRequest("/api/sponsors", "POST", body);
    },
    onSuccess: (sponsor: Sponsor) => {
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      queryClient.invalidateQueries({ queryKey: ["/api/sponsors"] });
      navigate(`/workbench/sponsors/${sponsor.id}`);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to create sponsor", variant: "destructive" });
    },
  });

  const updateSponsor = useMutation({
    mutationFn: async () => {
      if (!editTarget) return;
      return apiRequest(`/api/sponsors/${editTarget.id}`, "PATCH", {
        name: form.name.trim(),
        website: form.website.trim() || null,
        notes: form.notes.trim() || null,
      });
    },
    onSuccess: () => {
      setEditTarget(null);
      queryClient.invalidateQueries({ queryKey: ["/api/sponsors"] });
      toast({ title: "Sponsor updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update sponsor", variant: "destructive" });
    },
  });

  const deleteSponsor = useMutation({
    mutationFn: async (id: string) => apiRequest(`/api/sponsors/${id}`, "DELETE"),
    onSuccess: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["/api/sponsors"] });
      toast({ title: "Sponsor deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to delete sponsor", variant: "destructive" });
    },
  });

  const filtered = sponsors.filter((s) =>
    s.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const openEdit = (sponsor: Sponsor) => {
    setForm({ name: sponsor.name, website: sponsor.website ?? "", notes: sponsor.notes ?? "" });
    setEditTarget(sponsor);
  };

  return (
    <WorkbenchLayout>
      {/* Page header */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shrink-0 shadow-sm">
            <Briefcase className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">New experience</p>
            <h1 className="text-3xl font-bold text-gray-900 leading-tight">Sponsor Workbench</h1>
            <p className="text-gray-500 mt-0.5">Your potential sponsors, and everything you have created for them, in one place.</p>
          </div>
        </div>
        <Button
          onClick={() => { setForm(EMPTY_FORM); setCreateOpen(true); }}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="button-new-sponsor"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Sponsor
        </Button>
      </div>

      {/* Dashboard stat blocks (same cards as the classic dashboard) */}
      <StatsCards />

      {/* Your sponsors */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Sponsors</h2>

      {/* Toolbar: search */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search sponsors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-gray-200 rounded-full"
            data-testid="input-sponsor-search"
          />
        </div>
        <p className="text-xs text-gray-400">
          {sponsors.length} sponsor{sponsors.length === 1 ? "" : "s"}, most recently worked first
        </p>
      </div>

      {/* Sponsor grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Skeleton className="h-36 w-full rounded-xl" />
          <Skeleton className="h-36 w-full rounded-xl" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed rounded-xl bg-white/50">
          <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          {sponsors.length === 0 ? (
            <>
              <p className="text-gray-700 font-medium">No sponsors yet</p>
              <p className="text-sm text-gray-400 mt-1">Add your first potential sponsor to start working it through the funnel.</p>
              <Button className="mt-4 bg-purple-600 hover:bg-purple-700" onClick={() => { setForm(EMPTY_FORM); setCreateOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                New Sponsor
              </Button>
            </>
          ) : (
            <>
              <p className="text-gray-500">No sponsors match "{search}".</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-3 text-gray-500"
                onClick={() => setSearch("")}
                data-testid="button-clear-search"
              >
                Clear search
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filtered.map((sponsor) => {
            return (
              <div
                key={sponsor.id}
                className="group rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all cursor-pointer"
                onClick={() => navigate(`/workbench/sponsors/${sponsor.id}`)}
                data-testid={`sponsor-card-${sponsor.id}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{sponsor.name}</h3>
                      {sponsor.website && (
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1 truncate">
                          <Globe className="h-3.5 w-3.5 shrink-0" />
                          {sponsor.website}
                        </p>
                      )}
                      {sponsor.notes && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">{sponsor.notes}</p>
                      )}
                    </div>
                    <div
                      className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="sm" onClick={() => openEdit(sponsor)} data-testid={`button-edit-sponsor-${sponsor.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(sponsor)} data-testid={`button-delete-sponsor-${sponsor.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-xs text-gray-400">
                      {sponsor.updatedAt
                        ? `Updated ${formatDistanceToNow(new Date(sponsor.updatedAt), { addSuffix: true })}`
                        : ""}
                    </p>
                    <span className="text-xs text-gray-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Open workbench <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) setForm(EMPTY_FORM); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Sponsor</DialogTitle>
            <DialogDescription>Add a potential sponsor to start working it through the funnel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sponsor-name">Sponsor Name <span className="text-red-500">*</span></Label>
              <Input
                id="sponsor-name"
                placeholder="e.g., 1-800-Flowers"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="input-sponsor-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sponsor-website">Website</Label>
              <Input
                id="sponsor-website"
                placeholder="1800flowers.com"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="input-sponsor-website"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sponsor-notes">Notes (Optional)</Label>
              <Textarea
                id="sponsor-notes"
                placeholder="Include any key talking points, context, guidance or required language."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="min-h-[80px]"
                data-testid="input-sponsor-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createSponsor.mutate()}
              disabled={createSponsor.isPending || !form.name.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="button-confirm-create-sponsor"
            >
              {createSponsor.isPending ? "Creating..." : "Create Sponsor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sponsor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-sponsor-name">Sponsor Name <span className="text-red-500">*</span></Label>
              <Input
                id="edit-sponsor-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="input-edit-sponsor-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-sponsor-website">Website</Label>
              <Input
                id="edit-sponsor-website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="input-edit-sponsor-website"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-sponsor-notes">Notes (Optional)</Label>
              <Textarea
                id="edit-sponsor-notes"
                placeholder="Include any key talking points, context, guidance or required language."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="min-h-[80px]"
                data-testid="input-edit-sponsor-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button
              onClick={() => updateSponsor.mutate()}
              disabled={updateSponsor.isPending || !form.name.trim()}
              data-testid="button-confirm-edit-sponsor"
            >
              {updateSponsor.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this sponsor?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? `"${deleteTarget.name}"` : ""} and its CNA analyses will be removed.
              Past VBRs and copy stay in the system but will no longer be linked to this sponsor.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteSponsor.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete-sponsor"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WorkbenchLayout>
  );
}
